/**
 * Step — Sync products and designs (Phase 65 / BLU-594 PR4). Product sync
 * owns the design-assignment + run surface (PR2/PR3); this step shows live
 * progress (print rows written + the last run's status) and links out via the
 * footer primary "Go to Product sync". Never duplicates that surface — the run path is a
 * human-gated select → plan → approve flow, so there is deliberately no
 * inline "run" mutation here.
 *
 * BLU-1371 / BLU-1373: the description follows the client's garments (the
 * 3PL's, both, or its own). A client that sells custom only writes no print
 * rows (custom products need no designs), so with 0 rows it gets a note saying
 * the count can stay at 0, and once its last sync finished with products done
 * the collapsed summary says so instead of "0 print rows".
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StepPane } from '../shell/StepPane';
import { DeepLinkGo, DeepLinkNote, DeepLinkStat } from './DeepLink';
import { customSyncJobDone } from '../../lib/steps';
import { JobStatusPill } from '@/features/product-sync/components/job/JobStatusPill';
import type { ProductSyncJobStatus } from '@/features/product-sync/lib/apply-types';
import type { BlankSource, SellsKind } from '@threadheads/shared';

interface Step3Props {
  lookupRows: number;
  /** `productsDone` is optional for deploy skew (a backend without it reads 0). */
  lastSyncJob: { id: string; status: string; finished_at: string | null; productsDone?: number } | null;
  /** The client's garment choice; picks the description. */
  blankSource?: BlankSource | null;
  /** What the client sells; `custom` swaps in the custom copy when there are no print rows. */
  sells?: SellsKind | null;
}

const DESCRIPTION_KEY: Record<BlankSource, string> = {
  supply: 'step3.descriptionSupply',
  both: 'step3.descriptionBoth',
  own: 'step3.description',
};

export function Step3Sync({
  lookupRows,
  lastSyncJob,
  blankSource = null,
  sells = null,
}: Step3Props) {
  const { t } = useTranslation('onboarding');

  const customNoRows = sells === 'custom' && lookupRows === 0;
  const customJobDone = customNoRows && customSyncJobDone({ sells, lastSyncJob });

  return (
    <StepPane
      title={t('step3.title')}
      description={t(blankSource ? DESCRIPTION_KEY[blankSource] : 'step3.description')}
      primary={<DeepLinkGo page="productSync" />}
      summary={customJobDone ? t('step3.summaryCustom') : t('step3.summary', { count: lookupRows })}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <DeepLinkStat value={lookupRows} label={t('step3.lookupRows', { count: lookupRows })} />
        {lastSyncJob && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('step3.lastRun')}</span>
            <JobStatusPill status={lastSyncJob.status as ProductSyncJobStatus} />
            <Link to={`/product-sync/jobs/${lastSyncJob.id}`} className="text-xs font-medium text-threadheads-purple hover:underline">
              {t('step3.viewRun')}
            </Link>
          </div>
        )}
      </div>
      {lookupRows === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          {customNoRows ? t('step3.emptyNoteCustom') : t('step3.emptyNote')}
        </p>
      )}
      <DeepLinkNote page="productSync" />
    </StepPane>
  );
}
